import { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Package, Loader2, ImageIcon, X } from 'lucide-react';

const ProductsPage = () => {
  const { API_URL, getAuthHeader } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    breads_per_box: 10,
    default_price: 0,
    cost_price: 0,
    image_base64: '',
    color: '#000000'
  });
  const fileInputRef = useRef(null);

  // Predefined colors for quick selection
  const colorOptions = [
    { name: 'أسود', value: '#000000' },
    { name: 'أحمر', value: '#dc2626' },
    { name: 'أخضر', value: '#16a34a' },
    { name: 'أزرق', value: '#2563eb' },
    { name: 'برتقالي', value: '#ea580c' },
    { name: 'بنفسجي', value: '#9333ea' },
    { name: 'وردي', value: '#db2777' },
    { name: 'بني', value: '#92400e' },
  ];

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/products`, {
        headers: getAuthHeader()
      });
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('حدث خطأ في تحميل المنتجات');
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setSelectedProduct(null);
    setFormData({ name: '', breads_per_box: 10, default_price: 0, cost_price: 0, image_base64: '', color: '#000000' });
    setDialogOpen(true);
  };

  const openEditDialog = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      breads_per_box: product.breads_per_box,
      default_price: product.default_price,
      cost_price: product.cost_price || 0,
      image_base64: product.image_base64 || '',
      color: product.color || '#000000'
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (product) => {
    setSelectedProduct(product);
    setDeleteDialogOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { // 2MB limit
        toast.error('حجم الصورة يجب أن يكون أقل من 2 ميجابايت');
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData({ ...formData, image_base64: reader.result });
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const dataToSend = { ...formData };
      // Don't send empty image_base64
      if (!dataToSend.image_base64) {
        delete dataToSend.image_base64;
      }
      
      if (selectedProduct) {
        await axios.put(
          `${API_URL}/api/products/${selectedProduct.id}`,
          dataToSend,
          { headers: getAuthHeader() }
        );
        toast.success('تم تحديث المنتج بنجاح');
      } else {
        await axios.post(
          `${API_URL}/api/products`,
          dataToSend,
          { headers: getAuthHeader() }
        );
        toast.success('تم إضافة المنتج بنجاح');
      }
      setDialogOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('حدث خطأ في حفظ المنتج');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(
        `${API_URL}/api/products/${selectedProduct.id}`,
        { headers: getAuthHeader() }
      );
      toast.success('تم حذف المنتج بنجاح');
      setDeleteDialogOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('حدث خطأ في حذف المنتج');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-10 h-10"></div>
      </div>
    );
  }

  return (
    <div data-testid="products-page" className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-stone-900">المنتجات</h1>
          <p className="text-stone-500 mt-1">{products.length} منتج</p>
        </div>
        <Button
          data-testid="add-product-btn"
          onClick={openAddDialog}
          className="bg-amber-600 hover:bg-amber-700 text-white font-bold"
        >
          <Plus className="w-5 h-5 me-2" />
          إضافة منتج
        </Button>
      </div>

      {products.length === 0 ? (
        <div className="empty-state">
          <Package className="empty-state-icon" />
          <h3 className="empty-state-title">لا يوجد منتجات</h3>
          <p className="empty-state-description">أضف منتجاً جديداً</p>
        </div>
      ) : (
        <Card className="border-stone-200">
          <Table>
            <TableHeader>
              <TableRow className="bg-stone-50">
                <TableHead className="text-right font-bold w-16">الصورة</TableHead>
                <TableHead className="text-right font-bold">اسم المنتج</TableHead>
                <TableHead className="text-right font-bold w-12">اللون</TableHead>
                <TableHead className="text-right font-bold">عدد الربطات</TableHead>
                <TableHead className="text-right font-bold">سعر البيع (€)</TableHead>
                <TableHead className="text-right font-bold">سعر الشراء (€)</TableHead>
                <TableHead className="text-right font-bold">الربح (€)</TableHead>
                <TableHead className="text-right font-bold">إجراءات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {products.map((product) => {
                const profit = product.default_price - (product.cost_price || 0);
                return (
                  <TableRow key={product.id} className="hover:bg-stone-50/50">
                    <TableCell>
                      {product.image_base64 ? (
                        <img 
                          src={product.image_base64} 
                          alt={product.name}
                          className="w-12 h-12 object-cover rounded-lg"
                        />
                      ) : (
                        <div className="w-12 h-12 bg-stone-100 rounded-lg flex items-center justify-center">
                          <Package className="w-6 h-6 text-stone-400" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium" style={{ color: product.color || '#000000' }}>
                      {product.name}
                    </TableCell>
                    <TableCell>
                      <div 
                        className="w-6 h-6 rounded-full border border-stone-200"
                        style={{ backgroundColor: product.color || '#000000' }}
                        title={product.color || '#000000'}
                      />
                    </TableCell>
                    <TableCell className="font-numbers">{product.breads_per_box}</TableCell>
                    <TableCell className="font-numbers">{product.default_price.toFixed(2)} €</TableCell>
                    <TableCell className="font-numbers">{(product.cost_price || 0).toFixed(2)} €</TableCell>
                    <TableCell className={`font-numbers font-bold ${profit >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {profit.toFixed(2)} €
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(product)}
                          className="text-stone-500 hover:text-amber-600"
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(product)}
                          className="text-stone-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{selectedProduct ? 'تعديل منتج' : 'إضافة منتج جديد'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Product Image */}
            <div className="space-y-2">
              <Label>صورة المنتج (للعرض في بوابة الزبون)</Label>
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
              />
              {formData.image_base64 ? (
                <div className="relative w-full h-40 bg-stone-100 rounded-lg overflow-hidden">
                  <img 
                    src={formData.image_base64} 
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="absolute top-2 left-2"
                    onClick={() => setFormData({ ...formData, image_base64: '' })}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="w-full h-32 border-dashed"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="flex flex-col items-center gap-2">
                    <ImageIcon className="w-8 h-8 text-stone-400" />
                    <span className="text-stone-500">اضغط لرفع صورة</span>
                  </div>
                </Button>
              )}
            </div>

            <div className="space-y-2">
              <Label>اسم المنتج *</Label>
              <Input
                data-testid="product-name-input"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="مثال: خبز عربي كبير"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>عدد الربطات بالصندوق</Label>
              <Input
                data-testid="product-breads-input"
                type="number"
                min="1"
                value={formData.breads_per_box}
                onChange={(e) => setFormData({ ...formData, breads_per_box: parseInt(e.target.value) || 1 })}
              />
            </div>
            <div className="space-y-2">
              <Label>السعر الافتراضي للبيع (€)</Label>
              <Input
                data-testid="product-price-input"
                type="number"
                min="0"
                step="0.01"
                value={formData.default_price}
                onChange={(e) => setFormData({ ...formData, default_price: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div className="space-y-2">
              <Label>سعر الشراء (€) - لحساب الأرباح</Label>
              <Input
                data-testid="product-cost-input"
                type="number"
                min="0"
                step="0.01"
                value={formData.cost_price}
                onChange={(e) => setFormData({ ...formData, cost_price: parseFloat(e.target.value) || 0 })}
              />
            </div>

            {/* Product Color */}
            <div className="space-y-2">
              <Label>لون المنتج (في ورقة التوصيل)</Label>
              <div className="flex gap-2 flex-wrap">
                {colorOptions.map((c) => (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, color: c.value })}
                    className={`w-8 h-8 rounded-full border-2 transition-all ${
                      formData.color === c.value ? 'border-amber-500 scale-110 shadow-lg' : 'border-transparent hover:scale-105'
                    }`}
                    style={{ backgroundColor: c.value }}
                    title={c.name}
                  />
                ))}
                <input
                  type="color"
                  value={formData.color}
                  onChange={(e) => setFormData({ ...formData, color: e.target.value })}
                  className="w-8 h-8 rounded cursor-pointer border-0"
                  title="اختر لون مخصص"
                />
              </div>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm text-stone-500">معاينة:</span>
                <span className="font-bold text-lg" style={{ color: formData.color }}>
                  {formData.name || 'اسم المنتج'}
                </span>
              </div>
            </div>

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                إلغاء
              </Button>
              <Button
                type="submit"
                data-testid="save-product-btn"
                className="bg-amber-600 hover:bg-amber-700"
                disabled={formLoading}
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : selectedProduct ? 'تحديث' : 'إضافة'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>هل أنت متأكد من حذف هذا المنتج؟</AlertDialogTitle>
            <AlertDialogDescription>
              سيتم حذف المنتج "{selectedProduct?.name}". هذا الإجراء لا يمكن التراجع عنه.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2">
            <AlertDialogCancel>إلغاء</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              حذف
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ProductsPage;
